import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { FiEdit2 } from 'react-icons/fi';
import DeleteIcon from '@mui/icons-material/Delete';
import logo from '../assets/log.png';
import bill from '../assets/bill.jpg';
import stamp from '../assets/stamp.jpg';
import { printWhenReady } from './printUtils';
import { Buffer } from 'buffer';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { QRCodeSVG } from 'qrcode.react';
import { renderToStaticMarkup } from 'react-dom/server';
import API_BASE_URL from '../api';
import DocumentNavigation from './DocumentNavigation';
import { buildQuotePrintHtml } from './quotePrint';
import { buildVatTrialPrintHtml } from './vatTrialPrint';
window.Buffer = Buffer; // Make it globally available
const InvoiceList = () => {
  const [invoices, setInvoices] = useState([]);
  const [listType, setListType] = useState('vat');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [viewDocument, setViewDocument] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [listError, setListError] = useState('');
  const [translatedFields, setTranslatedFields] = useState({});

  const fetchInvoices = useCallback(async () => {
    setIsLoading(true);
    setListError('');
    const endpoint = {
      vat: '/invoice',
      quote: '/quote',
      trial: '/vat-trial'
    }[listType];
    try {
      const res = await axios.get(`${API_BASE_URL}${endpoint}`);
      setInvoices(res.data);
    } catch (err) {
      console.error(`Error fetching ${listType} documents:`, err);
      setInvoices([]);
      setListError(err.response?.data?.error || `Unable to fetch ${listType} list. Please try again.`);
    } finally {
      setIsLoading(false);
    }
  }, [listType]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);


  const handleEditClick = async (id) => {
    setIsLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/invoice/${id}`);
      setSelectedInvoice(res.data);
      setOpenDialog(true);
      // Initialize translated fields
      const initialTranslations = {};
      res.data.products.forEach((product, index) => {
        initialTranslations[`natureOfGoods_${index}`] = '';
        initialTranslations[`productDescription_${index}`] = '';
      });
      setTranslatedFields(initialTranslations);
    } catch (err) {
      console.error('Error fetching invoice:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setSelectedInvoice(null);
    setTranslatedFields({});
  };

  const handleQuotePrint = async (quote) => {
    const printWindow = window.open('', '_blank', 'height=800,width=1000');
    if (!printWindow) {
      alert('The print window was blocked. Please allow pop-ups and try again.');
      return;
    }
    printWindow.document.write(buildQuotePrintHtml(quote));
    printWindow.document.close();
    await printWhenReady(printWindow);
  };

  const handleTrialPrint = async (trial) => {
    const printWindow = window.open('', '_blank', 'height=800,width=1000');
    if (!printWindow) {
      alert('The print window was blocked. Please allow pop-ups and try again.');
      return;
    }
    try {
      printWindow.document.write(buildVatTrialPrintHtml(trial));
      printWindow.document.close();
      await printWhenReady(printWindow);
    } catch (error) {
      console.error('Unable to print VAT Trial:', error);
      printWindow.close();
      alert(error.message || 'Unable to print the VAT Trial. Please try again.');
    }
  };

  const handleUpdateInvoice = async () => {
    try {
      await axios.put(
        `${API_BASE_URL}/invoice/${selectedInvoice._id}`,
        selectedInvoice
      );
      alert('Invoice updated successfully!');
      fetchInvoices();
      handleDialogClose();
    } catch (err) {
      console.error('Error updating invoice:', err);
      alert('Failed to update invoice.');
    }
  };
  

  const handleInputChange = (e, section) => {
    const { name, value } = e.target;
    if (section === 'client') {
      setSelectedInvoice({
        ...selectedInvoice,
        clientDetails: {
          ...selectedInvoice.clientDetails,
          [name]: value,
        },
      });
    } else if (section === 'invoice') {
      setSelectedInvoice({
        ...selectedInvoice,
        invoiceDetails: {
          ...selectedInvoice.invoiceDetails,
          [name]: value,
        },
      });
    }
  };

  const handleProductChange = (e, index) => {
    const { name, value } = e.target;
    const updatedProducts = [...selectedInvoice.products];
    updatedProducts[index][name] = value;

    // Recalculate amounts if unitRate, quantity, or vatRate changes
    if (name === 'unitRate' || name === 'quantity' || name === 'vatRate') {
      const unitRate = parseFloat(updatedProducts[index].unitRate) || 0;
      const quantity = parseFloat(updatedProducts[index].quantity) || 0;
      const vatRate = parseFloat(updatedProducts[index].vatRate) || 0;

      const totalExclVAT = unitRate * quantity;
      const vatAmount = (totalExclVAT * vatRate) / 100;
      const totalInclVAT = totalExclVAT + vatAmount;

      updatedProducts[index].totalExclVAT = totalExclVAT.toFixed(2);
      updatedProducts[index].vatAmount = vatAmount.toFixed(2);
      updatedProducts[index].totalInclVAT = totalInclVAT.toFixed(2);
    }

    setSelectedInvoice({
      ...selectedInvoice,
      products: updatedProducts,
      totals: {
        totalExclVAT: updatedProducts.reduce((acc, product) => acc + parseFloat(product.totalExclVAT || 0), 0).toFixed(2),
        totalVAT: updatedProducts.reduce((acc, product) => acc + parseFloat(product.vatAmount || 0), 0).toFixed(2),
        totalInclVAT: updatedProducts.reduce((acc, product) => acc + parseFloat(product.totalInclVAT || 0), 0).toFixed(2),
        balanceDue: updatedProducts.reduce((acc, product) => acc + parseFloat(product.totalInclVAT || 0), 0).toFixed(2),
      },
    });
  };

  const addProductRow = () => {
    const newProducts = [
      ...selectedInvoice.products,
      {
        natureOfGoods: '',
        unitRate: '',
        quantity: '',
        vatRate: '15.00',
        totalExclVAT: 0,
        vatAmount: 0,
        totalInclVAT: 0,
        productDescription: '',
      },
    ];
    
    setSelectedInvoice({
      ...selectedInvoice,
      products: newProducts,
    });
    
    // Add new translation fields for the new product
    const newIndex = newProducts.length - 1;
    setTranslatedFields({
      ...translatedFields,
      [`natureOfGoods_${newIndex}`]: '',
      [`productDescription_${newIndex}`]: '',
    });
  };

  const removeProductRow = (index) => {
    const updatedProducts = selectedInvoice.products.filter((_, i) => i !== index);
    setSelectedInvoice({
      ...selectedInvoice,
      products: updatedProducts,
      totals: {
        totalExclVAT: updatedProducts.reduce((acc, product) => acc + parseFloat(product.totalExclVAT || 0), 0).toFixed(2),
        totalVAT: updatedProducts.reduce((acc, product) => acc + parseFloat(product.vatAmount || 0), 0).toFixed(2),
        totalInclVAT: updatedProducts.reduce((acc, product) => acc + parseFloat(product.totalInclVAT || 0), 0).toFixed(2),
        balanceDue: updatedProducts.reduce((acc, product) => acc + parseFloat(product.totalInclVAT || 0), 0).toFixed(2),
      },
    });
    
    // Remove the translation fields for the deleted product
    const newTranslatedFields = {...translatedFields};
    delete newTranslatedFields[`natureOfGoods_${index}`];
    delete newTranslatedFields[`productDescription_${index}`];
    setTranslatedFields(newTranslatedFields);
  };

  // Translation function


  // Transliteration function for Arabic names
  const transliterateToArabic = (text) => {
    const transliterationMap = {
      'a': 'ا', 'b': 'ب', 'c': 'ك', 'd': 'د', 'e': 'ي', 'f': 'ف',
      'g': 'ج', 'h': 'ه', 'i': 'ي', 'j': 'ج', 'k': 'ك', 'l': 'ل',
      'm': 'م', 'n': 'ن', 'o': 'و', 'p': 'ب', 'q': 'ق', 'r': 'ر',
      's': 'س', 't': 'ت', 'u': 'و', 'v': 'ف', 'w': 'و', 'x': 'كس',
      'y': 'ي', 'z': 'ز',
      'A': 'ا', 'B': 'ب', 'C': 'ك', 'D': 'د', 'E': 'ي', 'F': 'ف',
      'G': 'ج', 'H': 'ه', 'I': 'ي', 'J': 'ج', 'K': 'ك', 'L': 'ل',
      'M': 'م', 'N': 'ن', 'O': 'و', 'P': 'ب', 'Q': 'ق', 'R': 'ر',
      'S': 'س', 'T': 'ت', 'U': 'و', 'V': 'ف', 'W': 'و', 'X': 'كس',
      'Y': 'ي', 'Z': 'ز',
      ' ': ' ', ',': '،', '.': '.',
    };

    return Array.from(text).map(char => transliterationMap[char] || char).join('');
  };

  // Predefined translations for common terms
  const translations = {
    "Nature of Goods": "طبيعة البضائع",
    "Manpower": "قوى عاملة",
    "manpower": "قوى عاملة",
    "Kingdom of Saudi Arabia": "المملكة العربية السعودية",
    "Saudi Arabia": "المملكة العربية السعودية",
    "Kindom of Saudi Arabia": "المملكة العربية السعودية",
    "KSA": "المملكة العربية السعودية",
  };

  // Helper function to get translations
  const translate = (text) => {
    const normalizedText = text.toLowerCase().trim();
    const foundTranslation = Object.keys(translations).find(
      key => key.toLowerCase().trim() === normalizedText
    );
    return foundTranslation ? translations[foundTranslation] : text;
  };
  const translateText = async (text) => {
  
    console.log(text)
       try {
         const response = await axios.post('https://translator-arabic-lc.onrender.com/arb', { text: text });
         if (response.status === 200) {
           console.log(response)
           return response.data.translated_text; // Return the translated text
         } else {
           throw new Error(`Failed to translate text. Status code: ${response.status}`);
         }
       } catch (error) {
         console.error('Error:', error);
       } finally {
         setIsLoading(false); // Hide loader
       }
     
     return text; // If no text or error, return the original text
   };

   const printInvoice = async () => {
    try {
      // Prepare data first
      const qrCodeData = getBillDetailsForQRCode(selectedInvoice);
      const qrCodeMarkup = renderToStaticMarkup(
        <QRCodeSVG value={qrCodeData} size={150} level="M" />
      );
      const translatedGoods = await translateText(selectedInvoice.products[0]?.natureOfGoods || '');
      const translatedDescription = await translateText(selectedInvoice.products[0]?.productDescription || '');
    
    // Rest of your code...
    // Prepare the HTML content for printing
    const htmlContent = `
      <html>
        <head>
          <title>Vat Invoice</title>
          <style>
            /* Your existing print styles */
            body {
              font-family: Arial, sans-serif;
              margin: 20px;
              padding: 20px;
            }
            table {
              width: 100%;
              max-width: 900px;
              border-collapse: collapse;
              margin-top: 20px;
              border: 1px solid #d8d2cc;
              margin-left: auto;
              margin-right: auto;
            }
            th, td {
              border: 1px solid #d8d2cc;
              padding: 6px;
              text-align: left;
              height: 20px; 
              line-height: 1;
            }
            th {
              background-color: #f3f0eb;
            }
            .Invoice {
              text-align: center;
              color: #1D2A5F;
              font-size: 20px;
              font-weight: 700;
              padding: 12px 0;
            }
            .invoice-title {
              text-align: center;
              font-size: 1.5rem;
              margin-bottom: 10px;
            }
            .bold {
              font-weight: bold;
            }
            .no-border {
              border: none;
            }
            .col1 {
              height: 40px;
              text-align: center;
            }
            .col2 {
              width: 140px; 
            }
            .col3 {
              height: 90px;
              text-align: center;
            }
            .col4 {
              width: 20%; 
            }
            .col5 {
              width: 24%; 
            }
            .col6 {
              width: 7%;
              text-align: center; 
            }
            .col7 {
              width: 23%; 
            }
            .col8 {
              width: 8%; 
            }
            .col9 {
              height: 30px;
            }
            .col10 {
              height: 30px;
              text-align: center;
            }
            .colgoods {
              width: 31%; 
            }
            .colQty {
              width: 4%;
              text-align: center; 
            }
            .collast{
              width: 20%;
            }
            .header-container {
              display: flex;
              justify-content: center;
              align-items: center;
              gap: 20px;
            }
            .logo-container img {
              max-width: 100px;
              height: auto;
            }
            .left-align {
              float: left;
            }
            .right-align {
              float: right;
              text-align: right;
            }
            .clearfix::after {
              content: "";
              display: table;
              clear: both;
            }
            .Headtitle td {
              padding: 4px; 
              background-color: #1D2A5F;
            }
            .Headtitle h3 {
              margin: 0; 
              padding: 3px; 
              line-height: 1.2; 
              color: white;
            }
            p {
              margin: 0; 
            }
            .signature-section {
              text-align: center;
              vertical-align: bottom;
              padding-bottom: 10px;
            }
            .signature-section td {
              height: 60px;
            }
            .new{
              color: white;
              text-align: center; 
              background-color: #1D2A5F;
            }
            .qr-section {
              width: 20%;
              text-align: center;
              vertical-align: middle;
              border-left: 2px solid black;
            }
            .text-section {
              width: 30%;
              text-align: center;
              vertical-align: middle;
              border-left: 2px solid black;
            }
            .bold {
              font-weight: bold;
            }
            .arabic {
              font-size: 12px;
              display: block;
              font-family: 'Arial', sans-serif;
            }
            .align-end {
              text-align: right;
              direction: rtl;
            }
            @media print {
              * {
                -webkit-print-color-adjust: exact; 
                color-adjust: exact;               
                print-color-adjust: exact;         
              }
              body {
                margin: 0;
                padding: 0;
              }
            }
          </style>
        </head>
        <body>
          <div class="header-container">
            <div style="text-align: right;">
              <h1 style="color: #1D2A5F;">LAR Contracting Company</h1>
            </div>
            <div class="logo-container">
              <img src="${bill}" alt="Logo Image" /> 
            </div>
          </div>
          <div>
            <p class="Invoice"><strong>Vat Invoice:</strong></br><span class="bold">فاتورة ضريبة القيمة المضافة</span></p>
            <div class="clearfix">
              <p class="left-align" style="color: #D55C36;"><strong>Invoice No:</strong> <span class="bold">${selectedInvoice.invoiceNumber}</span></p>
              <p class="right-align" style="color: #D55C36;"><strong>رقم الفاتورة:</strong> <span class="bold">${selectedInvoice.invoiceNumber}</span></p>
            </div>
          </div>
           <table class="Detail">
        <tr class="Headtitle">
            <td colspan="8">
                <div class="clearfix">
                <h3 class="left-align"><strong><u>Our Details:</u></strong></h3>
                <h3 class="right-align" "dir="rtl"><strong><u>تفاصيلنا:</u></strong></h3>
                </div>
            </td>
        </tr>
       <tr>
    <th class="col2">Name:</th>
    <td colspan="3" class="col2">LAR Contracting Company</td>
    <td colspan="3" class="col2" style="text-align: end;">شركة إل آر كونتراكتنج</td>
    <th class="col2 align-end">الاسم:</th>
</tr>
<tr>
    <th class="col2">Short Address:</th>
    <td colspan="3" class="col2">ENAA8599</td>
    <td colspan="3" class="col2" style="text-align: end;">ENAA8599</td>
    <th class="col2 align-end">العنوان المختصر:</th>
</tr>
<tr>
    <th class="col2">Secondary No:</th>
    <td colspan="3" class="col2">2287</td>
    <td colspan="3" class="col2"  style="text-align: end;">2287</td> 
    <th class="col2 align-end">صندوق البريد:</th>
</tr>
        <tr>
            <th class="col2">Building No:</th>
            <td class="col2">8599</td>
            <th class="col2">City:</th>
            <td> Prince Saud Ibn Abdullah Ibn Jalawi Branch, AL JUBAIL</td>
           <td  style="text-align: end;" >فرع الأمير سعود بن عبدالله بن جلوي، الجبيل</td>
            <th class="col2 align-end">المدينة:</th>
            <td style="text-align: end;">الجبيل</td>
            <th class="col2 align-end">رقم المبنى:</th>
        </tr>
        <tr>
            <th class="col2">Customer Account:</th>
            <td>31335115107</td>
            <th class="col2">District:</th>
            <td>Tebah Dist.</td>
            <td style="text-align: end;">حي طيبة</td>
            <th class="col2 align-end"> الحي:</th>
            <td style="text-align: end;">31335115107</td>
            <th class="col2 align-end">رقم حساب العميل:</th>
        </tr>
            <tr>
            <th class="col2">Postal Code:</th>
            <td>35513</td>
            <th class="col2">Country:</th>
            <td>Kingdom of Saudi Arabia</td>
            <td style="text-align: end; width:10%;">المملكة العربية السعودية</td>
            <th class="col2 align-end"> الدولة:</th>
            <td style="text-align: end;">35513</td>
            <th class="col2 align-end">الرمز البريدي:</th>
        </tr>
        <tr>
            <th class="col2">Vat Number:</th>
            <td>314702232800003</td>
            <th class="col2">CRN:</th>
            
            <td colspan="2" style="text-align:center;">7053932963</td>
            <th class="col2 align-end"> السجل التجاري:</th>
            <td style="text-align: end;">314702232800003</td>
            <th class="col2 align-end">رقم ضريبة القيمة المضافة:</th>
        </tr>
    </table>
       
    <table class="Detail">
     <tr class="Headtitle">
         <td colspan="8" class="col2">
             <div class="clearfix">
             <h3 class="left-align"><strong><u>Client Details:</u></strong></h3>
             <h3 class="right-align" dir="rtl"><strong><u>تفاصيل العميل:</u></strong></h3>
             </div>
         </td>
     </tr>
     <tr>
         <th class="col2">Name:</th>
         <td colspan="3" class="col2">${selectedInvoice.clientDetails.clientName}</td>
         <td colspan="3" class="col2"  style="text-align: end;"> ${transliterateToArabic(selectedInvoice.clientDetails.clientName)}</td>
         <th class="col2 align-end">اسم:</th>
     </tr>
     <tr>
         <th class="col9">Address:</th>
         <td colspan="3" class="col9"> ${selectedInvoice.clientDetails.clientAddress}</td>
         <td colspan="3" class="col9"  style="text-align: end;"> ${transliterateToArabic(selectedInvoice.clientDetails.clientAddress)}</td> 
         <th class="col9 align-end">عنوان:</th>
     </tr>
    <tr>
         <th class="col2">Addl. No:</th>
         <td>${selectedInvoice.clientDetails.clientAddNo}</td>
         <th class="col2">District:</th>
         <td style="width : 10%;">${selectedInvoice.clientDetails.clientDistrict}</td>
         <td  style="text-align: end; width : 10%;">${transliterateToArabic(selectedInvoice.clientDetails.clientDistrict)}</td>
         <th class="col2 align-end">الحي:</th>
         <td  style="text-align: end;">${selectedInvoice.clientDetails.clientAddNo}</td>
         <th class="col2 align-end">الرقم الإضافي:</th>
     </tr>
     <tr>
         <th class="col2">Postal Code:</th>
         <td>${selectedInvoice.clientDetails.clientPostalCode}</td>
         <th class="col2">Country:</th>
         <td> ${selectedInvoice.clientDetails.clientCountry}</td>
         <td  style="text-align: end;">${translate(selectedInvoice.clientDetails.clientCountry)}</td>
         <th class="col2 align-end">دولة:</th>
         <td  style="text-align: end;">${transliterateToArabic(selectedInvoice.clientDetails.clientPostalCode)}</td>
         <th class="col2 align-end">الرمز البريدي:</th>
     </tr>
   <tr>
         <th class="col2">Vat Number:</th>
         <td>${selectedInvoice.clientDetails.clientVatNumber}</td>
         <th class="col2">CRN:</th>
         <td colspan="2" style="text-align:center;">${selectedInvoice.clientDetails.clientCRN}</td>
         <th class="col2 align-end">السجل التجاري:</th>
         <td  style="text-align: end;">${selectedInvoice.clientDetails.clientVatNumber}</td>
         <th class="col2 align-end">رقم ضريبة القيمة المضافة: </th>
     </tr>

 <table class="Detail">
     <tr>
         <th class="col1">  تاريخ الفاتورة <br>Invoice Date</th>
         
         <th class="col1">تاريخ التوريد <br>Supply Date</th>
         
         <th class="col1">   رقم العقد / أمر الشراء  <br>Contract/PO No</th>
        
         <th class="col1">  فترة الفاتورة <br>Invoice Period</th>
        
         <th class="col1"> رقم المشروع / المرجع  <br>Project/Reference No</th>
        
     </tr>
     <tr>
    <td class="col2" style="text-align: center;">
  ${selectedInvoice.invoiceDetails.invoiceDate ? new Date(selectedInvoice.invoiceDetails.invoiceDate).toLocaleDateString('en-GB') : ''}
</td>
<td class="col2" style="text-align: center;">
  ${selectedInvoice.invoiceDetails.supplyDate ? new Date(selectedInvoice.invoiceDetails.supplyDate).toLocaleDateString('en-GB') : ''}
</td>

         <td class="col2" style="text-align: center;">${selectedInvoice.invoiceDetails.contractNo}</td>
         <td class="col2" style="text-align: center;">${selectedInvoice.invoiceDetails.invoicePeriod}</td>
         <td class="col2" style="text-align: center;">${selectedInvoice.invoiceDetails.projectRefNo}</td>
     </tr>
 </table>
          <!-- Rest of your invoice HTML template -->
          <!-- You'll need to include all the sections from your demoUnit.js file -->
          <!-- This is a simplified version - you should include all the details -->

          <!-- Client Details Section -->
    

          <!-- Products Section -->
          <table class="Detail">
            <thead>
              <tr>
                <th class="col3"> الإجمالي شامل الضريبة (ريال سعودي) <br>Total Incl. VAT (SAR):</th>
                <th class="col3"> مبلغ ضريبة القيمة المضافة (ريال سعودي) <br>VAT Amount (SAR):</th>
                <th class="col6"> نسبة الضريبة (%) <br>VAT %:</th>
                <th class="col3"> الإجمالي بدون ضريبة (ريال سعودي) <br>Total Excl. VAT (SAR):</th>
                <th class="col3"> سعر الوحدة (ريال سعودي)<br>Unit Rate (SAR):</th>
                <th class="col6"> الكمية <br>Qty <br> /Hours:</th>
                <th class="col3"> وحدة <br>Unit/Hour:</th>
                <th class="colgoods" style="text-align: center;"> طبيعة السلع/الخدمات <br>Nature of Goods/Services:(Description)</th>
                <th class="col6">#</th>
              </tr>
            </thead>
            <tbody>
              ${selectedInvoice.products.map((product, index) => `
                <tr>
                  <td class="col1">${product.totalInclVAT}</td>
                  <td class="col1">${product.vatAmount}</td>
                  <td class="col1">${product.vatRate}</td>
                  <td class="col1">${product.totalExclVAT}</td>
                  <td class="col1">${product.unitRate}</td>
                  <td class="col1">${product.quantity}</td>
                  <td class="col1">${product.unit || 'UNIT'}</td>
                  <td class="col1">
                    ${product.natureOfGoods} / ${translatedGoods}
                  </td>
                  <td class="col1">${index + 1}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>

          <!-- Summary Section -->
          <table class="Summary">
            <tr>
              <td class="col4">Total Excl. VAT (SAR):</td>
              <td class="col8">${selectedInvoice.totals.totalExclVAT}</td>
              <td class="col5">الإجمالي بدون ضريبة (ريال سعودي)</td>
              <td colspan="4" rowspan="2" class="text-section">
                ${selectedInvoice.products[0]?.productDescription || ''}
              </td>
            </tr>
            <tr>
              <td class="col4">VAT Amount (SAR):</td>
              <td class="col8">${selectedInvoice.totals.totalVAT}</td>
              <td class="col5">مبلغ ضريبة القيمة المضافة (ريال سعودي)</td>
            </tr>
            <tr>
              <td class="col4">Total Incl. VAT (SAR):</td>
              <td class="col8">${selectedInvoice.totals.totalInclVAT}</td>
              <td class="col5">الإجمالي شامل الضريبة (ريال سعودي)</td>
               <td colspan="3" rowspan="2" class="signature-section">
              ${translatedDescription}
              </td>
            </tr>
            <tr>
              <td class="col4">Balance Due (SAR):</td>
              <td class="col8">${selectedInvoice.totals.balanceDue}</td>
              <td class="col5">الرصيد المستحق (ر.س)</td>
            </tr>
          </table>

          <!-- Bank Details Section -->
        
           <table>
     <tr>
         <th colspan="4" class="new">Bank Details / التفاصيل المصرفية</th>
         <th colspan="2" class="new">Prepared By / معد الفاتورة</th>
         <th colspan="2" class="new">Received By / مستلم الفاتورة</th>
         <td rowspan="6" class="qr-section">
             <!-- QR Code Frame -->
             <div id="qrcode">${qrCodeMarkup}</div>
             
         </td>
     </tr>
     <tr>
         <td colspan="2"><strong>ACCOUNT NAME</strong> / اسم الحساب</td>
         <td colspan="2" class="collast" >LAR Contracting Company<br>شركة إل آر كونتراكتنج</td>
         <td rowspan="5" class="signature-section" style="height:140px;padding:0;">
             <img src="${stamp}" alt="Signature with stamp" style="width:100%;height:140px;object-fit:fill;display:block;" />
         </td>
         <td colspan="3" rowspan="5" class="signature-section"><span class="arabic">&nbsp;</span></td>
     </tr>
     <tr>
         <td colspan="2"><strong>BANK</strong> / بنك</td>
         <td colspan="2" class="collast">Al Rajhi Bank</td>
     </tr>
     <tr>
         <td colspan="2"><strong>ACCOUNT</strong> / الحساب</td>
         <td colspan="2" class="collast">51300-001-0006085423395</td>
     </tr>
     <tr>
         <td colspan="2"><strong>IBAN</strong> / ايبان</td>
         <td colspan="2" class="collast">SA5380000513608015423395</td>
         
     </tr>
     <tr>
         <td colspan="2"><strong></strong> </td>
         <td colspan="2" class="collast"></td>
     </tr>
 </table>

        </body>
      </html>
    `;

    const printWindow = window.open('', '', 'height=600,width=800');
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    
    await printWhenReady(printWindow);
  } catch (error) {
    console.error('Error printing invoice:', error);
  }
};
 // QR Code Generation Functions
const generateTLV = (tag, value) => {
  const encoder = new TextEncoder();
  const encoded = encoder.encode(value);
  const valueHex = Array.from(encoded)
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
  
  const lengthHex = (value.length).toString(16).padStart(2, '0');
  const tagHex = tag.toString(16).padStart(2, '0');
  return tagHex + lengthHex + valueHex;
};

const getBillDetailsForQRCode = (invoice) => {
  const sellerName = "LAR Contracting Company";
  const vatRegistrationNumber = "314702232800003";
  const invoiceDate = invoice.invoiceDetails.invoiceDate || '';

  const totalAmount = invoice.products
    .reduce((acc, product) => acc + (parseFloat(product.totalInclVAT) || 0), 0)
    .toFixed(2);
  
  const vatAmount = invoice.products
    .reduce((acc, product) => acc + (parseFloat(product.vatAmount) || 0), 0)
    .toFixed(2);

  const tlvEncoded = 
    generateTLV(1, sellerName) +
    generateTLV(2, vatRegistrationNumber) +
    generateTLV(3, invoiceDate) +
    generateTLV(4, totalAmount) +
    generateTLV(5, vatAmount);

  // Convert hex to Base64 without Buffer
  const hexToBase64 = (hexString) => {
    return btoa(hexString.match(/\w{2}/g).map(a => 
      String.fromCharCode(parseInt(a, 16))
    ).join(''));
  };

  return hexToBase64(tlvEncoded);
};
const handlePrintInvoice = async (invoiceId) => {
  let printWindow;
  try {
    printWindow = window.open('', '', 'height=600,width=800');
    if (!printWindow) {
      alert('The print window was blocked. Please allow pop-ups and try again.');
      return;
    }
    
    const invoiceToPrint = invoices.find((invoice) => invoice._id === invoiceId);
    if (!invoiceToPrint) {
      throw new Error('Invoice data is not available. Refresh the VAT list and try again.');
    }
    
    const htmlContent = getInvoiceHtmlContent(invoiceToPrint);
    
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    await printWhenReady(printWindow);
  } catch (err) {
    console.error('Error printing invoice:', err);
    printWindow?.close();
    alert(err.message || err.response?.data?.error || 'Unable to print the invoice. Please try again.');
  }
};
  
  // Function to generate the complete HTML content for printing
  const getInvoiceHtmlContent = (invoice) => {
    const qrCodeMarkup = renderToStaticMarkup(
      <QRCodeSVG value={getBillDetailsForQRCode(invoice)} size={150} level="M" />
    );
    // Helper function to transliterate English to Arabic (keeps numbers as-is)
  const transliterateToArabic = (text) => {
      if (!text) return '';
      
      const transliterationMap = {
        'a': 'ا', 'b': 'ب', 'c': 'ك', 'd': 'د', 'e': 'ي', 'f': 'ف',
        'g': 'ج', 'h': 'ه', 'i': 'ي', 'j': 'ج', 'k': 'ك', 'l': 'ل',
        'm': 'م', 'n': 'ن', 'o': 'و', 'p': 'ب', 'q': 'ق', 'r': 'ر',
        's': 'س', 't': 'ت', 'u': 'و', 'v': 'ف', 'w': 'و', 'x': 'كس',
        'y': 'ي', 'z': 'ز',
        'A': 'ا', 'B': 'ب', 'C': 'ك', 'D': 'د', 'E': 'ي', 'F': 'ف',
        'G': 'ج', 'H': 'ه', 'I': 'ي', 'J': 'ج', 'K': 'ك', 'L': 'ل',
        'M': 'م', 'N': 'ن', 'O': 'و', 'P': 'ب', 'Q': 'ق', 'R': 'ر',
        'S': 'س', 'T': 'ت', 'U': 'و', 'V': 'ف', 'W': 'و', 'X': 'كس',
        'Y': 'ي', 'Z': 'ز',
        ' ': ' ', ',': '،', '.': '.',
      };
  
      return text.split('').map(char => {
        // Keep numbers as they are
        if (!isNaN(char) && char !== ' ') return char;
        return transliterationMap[char] || char;
      }).join('');
    };
  
    // Predefined translations for common terms
    const translations = {
      "Nature of Goods": "طبيعة البضائع",
      "Manpower": "قوى عاملة",
      "manpower": "قوى عاملة",
      "Kingdom of Saudi Arabia": "المملكة العربية السعودية",
      "Saudi Arabia": "المملكة العربية السعودية",
      "Kindom of Saudi Arabia": "المملكة العربية السعودية",
      "KSA": "المملكة العربية السعودية",
    };

    // Helper function to get translations
    
  const translate = (text) => {
      if (!text) return '';
      const normalizedText = text.toString().toLowerCase().trim();
      const foundTranslation = Object.keys(translations).find(
        key => key.toLowerCase().trim() === normalizedText
      );
      return foundTranslation ? translations[foundTranslation] : text;
    };

  const translatedGoods = transliterateToArabic(invoice.products[0]?.natureOfGoods || '');
  const amountDescription = invoice.products?.[0]?.productDescription || invoice.productDescription || '';
  const amountWordsArabic = {
    zero: 'صفر',
    one: 'واحد',
    two: 'اثنان',
    three: 'ثلاثة',
    four: 'أربعة',
    five: 'خمسة',
    six: 'ستة',
    seven: 'سبعة',
    eight: 'ثمانية',
    nine: 'تسعة',
    ten: 'عشرة',
    eleven: 'أحد عشر',
    twelve: 'اثنا عشر',
    thirteen: 'ثلاثة عشر',
    fourteen: 'أربعة عشر',
    fifteen: 'خمسة عشر',
    sixteen: 'ستة عشر',
    seventeen: 'سبعة عشر',
    eighteen: 'ثمانية عشر',
    nineteen: 'تسعة عشر',
    twenty: 'عشرون',
    thirty: 'ثلاثون',
    forty: 'أربعون',
    fifty: 'خمسون',
    sixty: 'ستون',
    seventy: 'سبعون',
    eighty: 'ثمانون',
    ninety: 'تسعون',
    hundred: 'مائة',
    thousand: 'ألف',
    million: 'مليون',
    billion: 'مليار',
    and: 'و',
    only: 'فقط',
    saudi: 'سعودي',
    riyal: 'ريال',
    riyals: 'ريالات',
    halala: 'هللة',
    halalas: 'هللات',
    halalah: 'هللة',
    halalahs: 'هللات'
  };
  const translatedDescription = String(amountDescription).replace(/[A-Za-z]+|[^A-Za-z]+/g, (part) => {
    const arabicWord = amountWordsArabic[part.toLowerCase()];
    return arabicWord || (/^[A-Za-z]+$/.test(part) ? transliterateToArabic(part) : part);
  });
  const escapePrintHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (character) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;'
  }[character]));
    
    return `
      <html>
        <head>
          <title>Vat Invoice</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              margin: 20px;
              padding: 20px;
            }
            table {
              width: 100%;
              max-width: 900px;
              border-collapse: collapse;
              margin-top: 20px;
              border: 1px solid #d8d2cc;
              margin-left: auto;
              margin-right: auto;
            }
            th, td {
              border: 1px solid #d8d2cc;
              padding: 6px;
              text-align: left;
              height: 20px; 
              line-height: 1;
            }
            th {
              background-color: #f3f0eb;
            }
            .Invoice {
              text-align: center;
              color: #1D2A5F;
              font-size: 20px;
              font-weight: 700;
              padding: 12px 0;
            }
            .invoice-title {
              text-align: center;
              font-size: 1.5rem;
              margin-bottom: 10px;
            }
            .bold {
              font-weight: bold;
            }
            .no-border {
              border: none;
            }
            .col1 {
              height: 40px;
              text-align: center;
            }
            .col2 {
              width: 140px; 
            }
            .col3 {
              height: 90px;
              text-align: center;
            }
            .col4 {
              width: 20%; 
            }
            .col5 {
              width: 24%; 
            }
            .col6 {
              width: 7%;
              text-align: center; 
            }
            .col7 {
              width: 23%; 
            }
            .col8 {
              width: 8%; 
            }
            .col9 {
              height: 30px;
            }
            .col10 {
              height: 30px;
              text-align: center;
            }
            .colgoods {
              width: 31%; 
            }
            .colQty {
              width: 4%;
              text-align: center; 
            }
            .collast{
              width: 20%;
            }
            .header-container {
              display: flex;
              justify-content: center;
              align-items: center;
              gap: 20px;
            }
            .logo-container img {
              max-width: 100px;
              height: auto;
            }
            .left-align {
              float: left;
            }
            .right-align {
              float: right;
              text-align: right;
            }
            .clearfix::after {
              content: "";
              display: table;
              clear: both;
            }
            .Headtitle td {
              padding: 4px; 
              background-color: #1D2A5F;
            }
            .Headtitle h3 {
              margin: 0; 
              padding: 3px; 
              line-height: 1.2; 
              color: white;
            }
            p {
              margin: 0; 
            }
            .signature-section {
              text-align: center;
              vertical-align: bottom;
              padding-bottom: 10px;
            }
            .signature-section td {
              height: 60px;
            }
            .new{
              color: white;
              text-align: center; 
              background-color: #1D2A5F;
            }
            .qr-section {
              width: 20%;
              text-align: center;
              vertical-align: middle;
              border-left: 2px solid black;
            }
            .text-section {
              width: 30%;
              text-align: center;
              vertical-align: middle;
              border-left: 2px solid black;
            }
            .bold {
              font-weight: bold;
            }
            .arabic {
              font-size: 12px;
              display: block;
              font-family: 'Arial', sans-serif;
            }
            .align-end {
              text-align: right;
              direction: rtl;
            }
            @media print {
              * {
                -webkit-print-color-adjust: exact; 
                color-adjust: exact;               
                print-color-adjust: exact;         
              }
              body {
                margin: 0;
                padding: 0;
              }
            }
          </style>
        </head>
        <body>
          <div class="header-container">
            <div style="text-align: right;">
              <h1 style="color: #1D2A5F;">LAR Contracting Company</h1>
            </div>
            <div class="logo-container">
              <img src="${bill}" alt="Logo Image" /> 
            </div>
          </div>
          <div>
            <p class="Invoice"><strong>Vat Invoice:</strong></br><span class="bold">فاتورة ضريبة القيمة المضافة</span></p>
            <div class="clearfix">
              <p class="left-align" style="color: #D55C36;"><strong>Invoice No:</strong> <span class="bold">${invoice.invoiceNumber}</span></p>
              <p class="right-align" style="color: #D55C36;"><strong>رقم الفاتورة:</strong> <span class="bold">${invoice.invoiceNumber}</span></p>
            </div>
          </div>
  
          <!-- Our Details Section -->
          <table class="Detail">
            <tr class="Headtitle">
              <td colspan="8">
                <div class="clearfix">
                  <h3 class="left-align"><strong><u>Our Details:</u></strong></h3>
                  <h3 class="right-align" dir="rtl"><strong><u>تفاصيلنا:</u></strong></h3>
                </div>
              </td>
            </tr>
            <tr>
              <th class="col2">Name:</th>
              <td colspan="3" class="col2">LAR Contracting Company</td>
              <td colspan="3" class="col2" style="text-align: end;">شركة إل آر كونتراكتنج</td>
              <th class="col2 align-end">الاسم:</th>
            </tr>
            <tr>
              <th class="col2">Short Address:</th>
              <td colspan="3" class="col2">ENAA8599</td>
              <td colspan="3" class="col2" style="text-align: end;">ENAA8599</td>
              <th class="col2 align-end">العنوان المختصر:</th>
            </tr>
            <tr>
              <th class="col2">Secondary No:</th>
              <td colspan="3" class="col2">2287</td>
              <td colspan="3" class="col2" style="text-align: end;">2287</td> 
              <th class="col2 align-end">صندوق البريد:</th>
            </tr>
            <tr>
              <th class="col2">Building No:</th>
              <td class="col2">8599</td>
              <th class="col2">City:</th>
              <td> Prince Saud Ibn Abdullah Ibn Jalawi Branch, AL JUBAIL</td>
              <td style="text-align: end;">فرع الأمير سعود بن عبدالله بن جلوي، الجبيل</td>
              <th class="col2 align-end">المدينة:</th>
              <td style="text-align: end;">الجبيل</td>
              <th class="col2 align-end">رقم المبنى:</th>
            </tr>
            <tr>
              <th class="col2">Customer Account:</th>
              <td> 31335115107</td>
              <th class="col2">District:</th>
              <td>Tebah Dist.</td>
              <td style="text-align: end;">حي طيبة</td>
              <th class="col2 align-end"> الحي:</th>
              <td style="text-align: end;"> 31335115107</td>
              <th class="col2 align-end">رقم حساب العميل:</th>
            </tr>
            <tr>
              <th class="col2">Postal Code:</th>
              <td>35513</td>
              <th class="col2">Country:</th>
              <td>Kingdom of Saudi Arabia</td>
              <td style="text-align: end; width:10%;">المملكة العربية السعودية</td>
              <th class="col2 align-end"> الدولة:</th>
              <td style="text-align: end;">35513</td>
              <th class="col2 align-end">الرمز البريدي:</th>
            </tr>
            <tr>
              <th class="col2">Vat Number:</th>
              <td>314702232800003</td>
              <th class="col2">CRN:</th>
              <td colspan="2" style="text-align:center;">7053932963</td>
              <th class="col2 align-end"> السجل التجاري:</th>
              <td style="text-align: end;">314702232800003</td>
              <th class="col2 align-end">رقم ضريبة القيمة المضافة:</th>
            </tr>
          </table>
  
          <!-- Client Details Section -->
          <table class="Detail">
            <tr class="Headtitle">
              <td colspan="8">
                <div class="clearfix">
                  <h3 class="left-align"><strong><u>Client Details:</u></strong></h3>
                  <h3 class="right-align" dir="rtl"><strong><u>تفاصيل العميل:</u></strong></h3>
                </div>
              </td>
            </tr>
            <tr>
              <th class="col2">Name:</th>
              <td colspan="3" class="col2">${invoice.clientDetails.clientName}</td>
              <td colspan="3" class="col2" style="text-align: end;">${transliterateToArabic(invoice.clientDetails.clientName)}</td>
              <th class="col2 align-end">اسم:</th>
            </tr>
            <tr>
              <th class="col9">Address:</th>
              <td colspan="3" class="col9">${invoice.clientDetails.clientAddress}</td>
              <td colspan="3" class="col9" style="text-align: end;">${transliterateToArabic(invoice.clientDetails.clientAddress)}</td> 
              <th class="col9 align-end">عنوان:</th>
            </tr>
            <tr>
              <th class="col2">Addl. No:</th>
              <td>${invoice.clientDetails.clientAddNo}</td>
              <th class="col2">District:</th>
              <td style="width : 10%;">${invoice.clientDetails.clientDistrict}</td>
              <td style="text-align: end; width : 10%;">${transliterateToArabic(invoice.clientDetails.clientDistrict)}</td>
              <th class="col2 align-end">الحي:</th>
              <td style="text-align: end;">${invoice.clientDetails.clientAddNo}</td>
              <th class="col2 align-end">الرقم الإضافي:</th>
            </tr>
            <tr>
              <th class="col2">Postal Code:</th>
              <td>${invoice.clientDetails.clientPostalCode}</td>
              <th class="col2">Country:</th>
              <td>${invoice.clientDetails.clientCountry}</td>
              <td style="text-align: end;">${translate(invoice.clientDetails.clientCountry)}</td>
              <th class="col2 align-end">دولة:</th>
              <td style="text-align: end;">${transliterateToArabic(invoice.clientDetails.clientPostalCode)}</td>
              <th class="col2 align-end">الرمز البريدي:</th>
            </tr>
            <tr>
              <th class="col2">Vat Number:</th>
              <td>${invoice.clientDetails.clientVatNumber}</td>
              <th class="col2">CRN:</th>
              <td colspan="2" style="text-align:center;">${invoice.clientDetails.clientCRN}</td>
              <th class="col2 align-end">السجل التجاري:</th>
              <td style="text-align: end;">${invoice.clientDetails.clientVatNumber}</td>
              <th class="col2 align-end">رقم ضريبة القيمة المضافة: </th>
            </tr>
          </table>
  
          <!-- Invoice Details Section -->
          <table class="Detail">
            <tr>
              <th class="col1">تاريخ الفاتورة <br>Invoice Date</th>
              <th class="col1">تاريخ التوريد <br>Supply Date</th>
              <th class="col1">رقم العقد / أمر الشراء <br>Contract/PO No</th>
              <th class="col1">فترة الفاتورة <br>Invoice Period</th>
              <th class="col1">رقم المشروع / المرجع <br>Project/Reference No</th>
            </tr>
            <tr>
            <td class="col2" style="text-align: center;">
  ${invoice.invoiceDetails.invoiceDate ? new Date(invoice.invoiceDetails.invoiceDate).toLocaleDateString('en-GB') : ''}
</td>
<td class="col2" style="text-align: center;">
  ${invoice.invoiceDetails.supplyDate ? new Date(invoice.invoiceDetails.supplyDate).toLocaleDateString('en-GB') : ''}
</td>

              <td class="col2" style="text-align: center;">${invoice.invoiceDetails.contractNo}</td>
              <td class="col2" style="text-align: center;">${invoice.invoiceDetails.invoicePeriod}</td>
              <td class="col2" style="text-align: center;">${invoice.invoiceDetails.projectRefNo}</td>
            </tr>
          </table>
  
          <!-- Products Section -->
          <table class="Detail">
            <thead>
              <tr>
                <th class="col3">الإجمالي شامل الضريبة (ريال سعودي) <br>Total Incl. VAT (SAR):</th>
                <th class="col3">مبلغ ضريبة القيمة المضافة (ريال سعودي) <br>VAT Amount (SAR):</th>
                <th class="col6">نسبة الضريبة (%) <br>VAT %:</th>
                <th class="col3">الإجمالي بدون ضريبة (ريال سعودي) <br>Total Excl. VAT (SAR):</th>
                <th class="col3">سعر الوحدة (ريال سعودي)<br>Unit Rate (SAR):</th>
                <th class="col6">الكمية <br>Qty <br>/Hours:</th>
                <th class="col3">وحدة <br>Unit/Hour:</th>
                <th class="colgoods" style="text-align: center;">طبيعة السلع/الخدمات <br>Nature of Goods/Services:(Description)</th>
                <th class="col6">#</th>
              </tr>
            </thead>
            <tbody>
              ${invoice.products.map((product, index) => `
                <tr>
                  <td class="col1">${product.totalInclVAT}</td>
                  <td class="col1">${product.vatAmount}</td>
                  <td class="col1">${product.vatRate}</td>
                  <td class="col1">${product.totalExclVAT}</td>
                  <td class="col1">${product.unitRate}</td>
                  <td class="col1">${product.quantity}</td>
                  <td class="col1">${product.unit || 'UNIT'}</td>
                  <td class="col1">
                    ${product.natureOfGoods}/ ${translatedGoods  }
                  </td>
                  <td class="col1">${index + 1}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
  
          <!-- Summary Section -->
          <table class="Summary">
            <tr>
              <td class="col4">Total Excl. VAT (SAR):</td>
              <td class="col8">${invoice.totals.totalExclVAT}</td>
              <td class="col5">الإجمالي بدون ضريبة (ريال سعودي)</td>
              <td colspan="4" rowspan="2" class="text-section">
                ${escapePrintHtml(amountDescription)}
              </td>
            </tr>
            <tr>
              <td class="col4">VAT Amount (SAR):</td>
              <td class="col8">${invoice.totals.totalVAT}</td>
              <td class="col5">مبلغ ضريبة القيمة المضافة (ريال سعودي)</td>
            </tr>
            <tr>
              <td class="col4">Total Incl. VAT (SAR):</td>
              <td class="col8">${invoice.totals.totalInclVAT}</td>
              <td class="col5">الإجمالي شامل الضريبة (ريال سعودي)</td>
              <td colspan="3" rowspan="2" class="signature-section">
              ${escapePrintHtml(translatedDescription)}
              </td>
            </tr>
            <tr>
              <td class="col4">Balance Due (SAR):</td>
              <td class="col8">${invoice.totals.balanceDue}</td>
              <td class="col5">الرصيد المستحق (ر.س)</td>
            </tr>
          </table>
  
          <!-- Bank Details Section -->
          <table>
            <tr>
              <th colspan="4" class="new">Bank Details / التفاصيل المصرفية</th>
              <th colspan="2" class="new">Prepared By / معد الفاتورة</th>
              <th colspan="2" class="new">Received By / مستلم الفاتورة</th>
              <td rowspan="6" class="qr-section">
                <div id="qrcode">${qrCodeMarkup}</div>
              </td>
            </tr>
            <tr>
              <td colspan="2"><strong>ACCOUNT NAME</strong> / اسم الحساب</td>
               <td colspan="2" class="collast" >LAR Contracting Company<br>شركة إل آر كونتراكتنج</td>
              <td rowspan="5" class="signature-section" style="height:140px;padding:0;">
                <img src="${stamp}" alt="Signature with stamp" style="width:100%;height:140px;object-fit:fill;display:block;" />
              </td>
              <td colspan="3" rowspan="5" class="signature-section">
                <span class="arabic">&nbsp;</span>
              </td>
            </tr>
            <tr>
         <td colspan="2"><strong>BANK</strong> / بنك</td>
         <td colspan="2" class="collast">Al Rajhi Bank</td>
     </tr>
     <tr>
         <td colspan="2"><strong>ACCOUNT</strong> / الحساب</td>
         <td colspan="2" class="collast">51300-001-0006085423395</td>
     </tr>
     <tr>
         <td colspan="2"><strong>IBAN</strong> / ايبان</td>
         <td colspan="2" class="collast">SA5380000513608015423395</td>
         
     </tr>
            <tr>
              <td colspan="2"><strong></strong> </td>
              <td colspan="2" class="collast"></td>
            </tr>
          </table>
        </body>
      </html>
    `;
  };
 const darkBlueTheme = createTheme({
      components: {
        MuiOutlinedInput: {
          styleOverrides: {
            root: {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#D8E0EA'
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#9EADC0'
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#E85D3F',
                borderWidth: 2
              }
            }
          }
        },
        MuiInputLabel: {
          styleOverrides: {
            root: {
              color: '#667085'
            },
            '&.Mui-focused': {
              color: '#C9432B'
            }
          }
        }
      }
    });
  return (
     <ThemeProvider theme={darkBlueTheme}>
    <div style={{ minHeight: '100vh', background: 'radial-gradient(circle at 10% 0%, rgba(232,93,63,.10), transparent 28%), linear-gradient(135deg, #EEF3F8 0%, #F8FAFC 52%, #EEF2F7 100%)', padding: '28px 14px 50px' }}>
    <div className="max-w-7xl mx-auto p-4">
      <DocumentNavigation />
      <div className="flex flex-wrap justify-center gap-2 mb-5" role="tablist" aria-label="Saved document lists">
        {[
          ['quote', 'Quote List'],
          ['vat', 'VAT List'],
          ['trial', 'VAT Trial List']
        ].map(([type, label]) => (
          <button
            key={type}
            type="button"
            role="tab"
            aria-selected={listType === type}
            onClick={() => setListType(type)}
            className={`px-5 py-2 rounded-full font-bold transition-colors ${
              listType === type
                ? 'bg-[#0B1F3A] text-white shadow-md'
                : 'bg-white text-[#0B1F3A] border border-slate-200 hover:bg-slate-100'
            }`}
          >
            {label}
          </button>
        ))}
      </div>
      <h2 className="text-2xl font-semibold mb-4 text-[#0B1F3A]">
        {listType === 'quote' ? 'Quote List' : listType === 'trial' ? 'VAT Trial List' : 'VAT List'}
      </h2>

      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : listError ? (
        <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-800">
          {listError}
        </div>
      ) : (
        <div className="overflow-x-auto bg-white rounded shadow">
          <table className="w-full text-sm text-left text-gray-700">
            <thead className="bg-[#0B1F3A] text-white uppercase text-xs">
              <tr>
                <th className="p-3">{listType === 'quote' ? 'Quote #' : listType === 'trial' ? 'Trial #' : 'Invoice #'}</th>
                <th className="p-3">Client Name</th>
                <th className="p-3">{listType === 'quote' ? 'Quote Date' : 'Invoice Date'}</th>
                <th className="p-3">Total Incl. VAT</th>
                <th className="p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {invoices.length > 0 ? (
                invoices.map((inv) => (
                  <tr key={inv._id} className="border-t hover:bg-gray-50">
                    <td className="p-3">{listType === 'quote' ? inv.quoteNumber : inv.invoiceNumber}</td>
                    <td className="p-3">
                      {listType === 'quote' ? inv.client?.name : listType === 'trial' ? inv.client?.clientName : inv.clientDetails?.clientName}
                    </td>
                    <td className="p-3">
                      {(() => {
                        const date = listType === 'quote'
                          ? inv.quoteDetails?.quoteDate
                          : listType === 'trial'
                            ? inv.invoice?.invoiceDate
                            : inv.invoiceDetails?.invoiceDate;
                        return date ? new Date(date).toLocaleDateString() : '—';
                      })()}
                    </td>
                    <td className="p-3">
                      {listType === 'quote' ? inv.totals?.total : inv.totals?.totalInclVAT} SAR
                    </td>
                    <td className="p-3">
                      <div className="flex flex-wrap gap-2">
                        {listType === 'vat' && (
                          <button
                            className="flex items-center gap-1 text-blue-600 hover:text-blue-800 p-1 rounded-full hover:bg-blue-50"
                            onClick={() => handleEditClick(inv._id)}
                            title="Edit"
                          >
                            <FiEdit2 size={16} />
                            EDIT
                          </button>
                        )}
                        <button
                          type="button"
                          className="px-4 py-2 bg-[#0B1F3A] text-white rounded hover:bg-[#173D68]"
                          onClick={() => setViewDocument({ type: listType, record: inv })}
                        >
                          View
                        </button>
                        <button
                          type="button"
                          className="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700"
                          onClick={() => {
                            if (listType === 'quote') handleQuotePrint(inv);
                            else if (listType === 'trial') handleTrialPrint(inv);
                            else handlePrintInvoice(inv._id);
                          }}
                        >
                          Print
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="text-center p-4 text-gray-500">
                    {listType === 'quote' ? 'No quotes found.' : listType === 'trial' ? 'No VAT Trials found.' : 'No invoices found.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {viewDocument && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black bg-opacity-50 p-4"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setViewDocument(null);
          }}
        >
          <section
            role="dialog"
            aria-modal="true"
            aria-labelledby="document-view-title"
            className="relative my-8 max-h-[90vh] w-full max-w-5xl overflow-y-auto rounded-xl bg-white p-6 shadow-xl"
          >
            <button
              type="button"
              onClick={() => setViewDocument(null)}
              className="absolute right-4 top-4 rounded px-3 py-1 text-gray-600 hover:bg-gray-100"
              aria-label="Close document details"
            >
              ✕
            </button>
            {viewDocument.type === 'quote' ? (
              <>
                <h2 id="document-view-title" className="mb-5 text-2xl font-bold text-[#0B1F3A]">
                  Quote #{viewDocument.record.quoteNumber}
                </h2>
                <h3 className="mb-2 font-bold text-[#0B1F3A]">Client Details</h3>
                <dl className="mb-5 grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {[
                    ['Name', viewDocument.record.client?.name],
                    ['Address', viewDocument.record.client?.address],
                    ['District', viewDocument.record.client?.district],
                    ['Postal Code', viewDocument.record.client?.postalCode],
                    ['Country', viewDocument.record.client?.country],
                    ['Additional No', viewDocument.record.client?.additionalNo],
                    ['VAT Number', viewDocument.record.client?.vatNumber],
                    ['CRN', viewDocument.record.client?.crn]
                  ].map(([label, value]) => (
                    <div key={label} className="rounded border p-2">
                      <dt className="text-xs font-semibold text-gray-500">{label}</dt>
                      <dd>{value || '—'}</dd>
                    </div>
                  ))}
                </dl>
                <h3 className="mb-2 font-bold text-[#0B1F3A]">Quote Details</h3>
                <dl className="mb-5 grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {Object.entries(viewDocument.record.quoteDetails || {}).map(([label, value]) => (
                    <div key={label} className="rounded border p-2">
                      <dt className="text-xs font-semibold capitalize text-gray-500">{label.replace(/([A-Z])/g, ' $1')}</dt>
                      <dd>{value || '—'}</dd>
                    </div>
                  ))}
                </dl>
                <h3 className="mb-2 font-bold text-[#0B1F3A]">Products / Services</h3>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-sm">
                    <thead className="bg-[#0B1F3A] text-white">
                      <tr>{['#', 'Description', 'Qty', 'Unit', 'Rate', 'Subtotal', 'VAT', 'Total'].map((label) => <th key={label} className="p-2 text-left">{label}</th>)}</tr>
                    </thead>
                    <tbody>
                      {(viewDocument.record.products || []).map((product, index) => (
                        <tr key={`${product.description}-${index}`} className="border-b">
                          <td className="p-2">{index + 1}</td>
                          <td className="p-2">{product.description || '—'}</td>
                          <td className="p-2">{product.quantity || '—'}</td>
                          <td className="p-2">{product.unit || '—'}</td>
                          <td className="p-2">{product.unitRate ?? '—'}</td>
                          <td className="p-2">{product.subtotal ?? '—'}</td>
                          <td className="p-2">{product.vatRate ?? '—'}%</td>
                          <td className="p-2">{product.total ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="mt-4 space-y-1 text-right font-semibold">
                  <p>Total Excl. VAT: {viewDocument.record.totals?.subtotal ?? '—'} SAR</p>
                  <p>Total VAT: {viewDocument.record.totals?.vat ?? '—'} SAR</p>
                  <p className="text-[#C9432B]">Total Incl. VAT: {viewDocument.record.totals?.total ?? '—'} SAR</p>
                </div>
              </>
            ) : viewDocument.type === 'trial' ? (
              <>
                <h2 id="document-view-title" className="mb-5 text-2xl font-bold text-[#0B1F3A]">
                  VAT Trial #{viewDocument.record.invoiceNumber}
                </h2>
                <h3 className="mb-2 font-bold text-[#0B1F3A]">Seller Details</h3>
                <dl className="mb-5 grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {Object.entries(viewDocument.record.seller || {}).map(([label, value]) => (
                    <div key={label} className="rounded border p-2">
                      <dt className="text-xs font-semibold capitalize text-gray-500">{label.replace(/([A-Z])/g, ' $1')}</dt>
                      <dd>{value || '—'}</dd>
                    </div>
                  ))}
                </dl>
                <h3 className="mb-2 font-bold text-[#0B1F3A]">Client Details</h3>
                <dl className="mb-5 grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {Object.entries(viewDocument.record.client || {}).map(([label, value]) => (
                    <div key={label} className="rounded border p-2">
                      <dt className="text-xs font-semibold capitalize text-gray-500">{label.replace(/^client/, '').replace(/([A-Z])/g, ' $1')}</dt>
                      <dd>{value || '—'}</dd>
                    </div>
                  ))}
                </dl>
                <h3 className="mb-2 font-bold text-[#0B1F3A]">Invoice Details</h3>
                <dl className="mb-5 grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {Object.entries(viewDocument.record.invoice || {}).map(([label, value]) => (
                    <div key={label} className="rounded border p-2">
                      <dt className="text-xs font-semibold capitalize text-gray-500">{label.replace(/([A-Z])/g, ' $1')}</dt>
                      <dd>{value || '—'}</dd>
                    </div>
                  ))}
                  <div className="rounded border p-2">
                    <dt className="text-xs font-semibold text-gray-500">Amount in Words</dt>
                    <dd>{viewDocument.record.productDescription || '—'}</dd>
                  </div>
                </dl>
                <h3 className="mb-2 font-bold text-[#0B1F3A]">Products / Services</h3>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-sm">
                    <thead className="bg-[#0B1F3A] text-white">
                      <tr>{['#', 'Nature of Goods', 'Unit', 'Qty', 'Rate', 'Excl. VAT', 'VAT', 'Incl. VAT'].map((label) => <th key={label} className="p-2 text-left">{label}</th>)}</tr>
                    </thead>
                    <tbody>
                      {(viewDocument.record.products || []).map((product, index) => (
                        <tr key={`${product.natureOfGoods}-${index}`} className="border-b">
                          <td className="p-2">{index + 1}</td>
                          <td className="p-2">{product.natureOfGoods || '—'}</td>
                          <td className="p-2">{product.unit || '—'}</td>
                          <td className="p-2">{product.quantity || '—'}</td>
                          <td className="p-2">{product.unitRate ?? '—'}</td>
                          <td className="p-2">{product.totalExclVAT ?? '—'}</td>
                          <td className="p-2">{product.vatAmount ?? '—'}</td>
                          <td className="p-2">{product.totalInclVAT ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="mt-4 space-y-1 text-right font-semibold">
                  <p>Total Excl. VAT: {viewDocument.record.totals?.totalExclVAT ?? '—'} SAR</p>
                  <p>Total VAT: {viewDocument.record.totals?.totalVAT ?? '—'} SAR</p>
                  <p className="text-[#C9432B]">Total Incl. VAT: {viewDocument.record.totals?.totalInclVAT ?? '—'} SAR</p>
                  <p>Balance Due: {viewDocument.record.totals?.balanceDue ?? '—'} SAR</p>
                </div>
              </>
            ) : (
              <>
                <h2 id="document-view-title" className="mb-5 text-2xl font-bold text-[#0B1F3A]">
                  VAT Invoice #{viewDocument.record.invoiceNumber}
                </h2>
                <h3 className="mb-2 font-bold text-[#0B1F3A]">Client Details</h3>
                <dl className="mb-5 grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {Object.entries(viewDocument.record.clientDetails || {}).map(([label, value]) => (
                    <div key={label} className="rounded border p-2">
                      <dt className="text-xs font-semibold capitalize text-gray-500">{label.replace(/^client/, '').replace(/([A-Z])/g, ' $1')}</dt>
                      <dd>{value || '—'}</dd>
                    </div>
                  ))}
                </dl>
                <h3 className="mb-2 font-bold text-[#0B1F3A]">Invoice Details</h3>
                <dl className="mb-5 grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {Object.entries(viewDocument.record.invoiceDetails || {}).map(([label, value]) => (
                    <div key={label} className="rounded border p-2">
                      <dt className="text-xs font-semibold capitalize text-gray-500">{label.replace(/([A-Z])/g, ' $1')}</dt>
                      <dd>{value || '—'}</dd>
                    </div>
                  ))}
                </dl>
                <h3 className="mb-2 font-bold text-[#0B1F3A]">Products / Services</h3>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-sm">
                    <thead className="bg-[#0B1F3A] text-white">
                      <tr>{['#', 'Nature of Goods', 'Description', 'Qty', 'Rate', 'Excl. VAT', 'VAT', 'Incl. VAT'].map((label) => <th key={label} className="p-2 text-left">{label}</th>)}</tr>
                    </thead>
                    <tbody>
                      {(viewDocument.record.products || []).map((product, index) => (
                        <tr key={`${product.natureOfGoods}-${index}`} className="border-b">
                          <td className="p-2">{index + 1}</td>
                          <td className="p-2">{product.natureOfGoods || '—'}</td>
                          <td className="p-2">{product.unit || '—'}</td>
                          <td className="p-2">{product.quantity || '—'}</td>
                          <td className="p-2">{product.unitRate ?? '—'}</td>
                          <td className="p-2">{product.totalExclVAT ?? '—'}</td>
                          <td className="p-2">{product.vatAmount ?? '—'}</td>
                          <td className="p-2">{product.totalInclVAT ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="mt-4 space-y-1 text-right font-semibold">
                  <p>Total Excl. VAT: {viewDocument.record.totals?.totalExclVAT ?? '—'} SAR</p>
                  <p>Total VAT: {viewDocument.record.totals?.totalVAT ?? '—'} SAR</p>
                  <p className="text-[#C9432B]">Total Incl. VAT: {viewDocument.record.totals?.totalInclVAT ?? '—'} SAR</p>
                  <p>Balance Due: {viewDocument.record.totals?.balanceDue ?? '—'} SAR</p>
                </div>
              </>
            )}
            <div className="mt-6 flex justify-end gap-2 border-t pt-4">
              <button type="button" onClick={() => setViewDocument(null)} className="rounded border px-4 py-2 hover:bg-gray-50">
                Close
              </button>
              <button
                type="button"
                onClick={() => {
                  if (viewDocument.type === 'quote') handleQuotePrint(viewDocument.record);
                  else if (viewDocument.type === 'trial') handleTrialPrint(viewDocument.record);
                  else handlePrintInvoice(viewDocument.record._id);
                }}
                className="rounded bg-orange-600 px-4 py-2 text-white hover:bg-orange-700"
              >
                Print
              </button>
            </div>
          </section>
        </div>
      )}

      {/* Edit Dialog */}
      {openDialog && selectedInvoice && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto p-4">
    <div className="bg-gray-100 rounded-lg mt-12 w-full max-w-5xl p-6 shadow-xl relative max-h-screen overflow-y-auto">
      <>
        <div className="max-w-[1000px] mx-auto my-5 p-5 bg-gray-100 rounded-[10px]">
          <img
            src={logo}
            alt="Logo"
            className="block mx-auto w-full h-auto"
          />
          <h1 className="text-4xl font-semibold text-center text-[#1D2A5F] mb-2">
            Invoice Generator
          </h1>
        </div>
      </>

      <button
        onClick={handleDialogClose}
        className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
      >
        ✕
      </button>

      <h3 className="text-lg font-semibold mb-4 text-center">
        Edit Invoice #{selectedInvoice?.invoiceNumber}
      </h3>

      {/* Our Details Section */}
      <h2 className="text-xl font-bold text-center mb-5 text-blue-900">
        Our Details
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="col-span-3 relative">
          <input
            className="peer p-2 pt-6 w-full border border-gray-300 rounded focus:outline-none focus:border-blue-500"
            value="LAR Contracting Company"
            readOnly
            placeholder=" "
          />
          <label className="absolute left-2 top-0 text-blue-500 text-sm transition-all peer-placeholder-shown:top-2 peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400 peer-focus:top-0 peer-focus:text-sm peer-focus:text-blue-500 bg-white px-1">
            Name
          </label>
        </div>

        <div className="relative">
          <input 
            className="peer p-2 pt-6 w-full border border-gray-300 rounded focus:outline-none focus:border-blue-500"
            value="2287" 
            readOnly
            placeholder=" "
          />
          <label className="absolute left-2 top-0 text-blue-500 text-sm transition-all peer-placeholder-shown:top-2 peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400 peer-focus:top-0 peer-focus:text-sm peer-focus:text-blue-500 bg-white px-1">
            Post Box No
          </label>
        </div>
        
        <div className="relative">
          <input 
            className="peer p-2 pt-6 w-full border border-gray-300 rounded focus:outline-none focus:border-blue-500"
            value="31335115107" 
            readOnly
            placeholder=" "
          />
          <label className="absolute left-2 top-0 text-blue-500 text-sm transition-all peer-placeholder-shown:top-2 peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400 peer-focus:top-0 peer-focus:text-sm peer-focus:text-blue-500 bg-white px-1">
            Addl. No
          </label>
        </div>
        
        <div className="relative">
          <input 
            className="peer p-2 pt-6 w-full border border-gray-300 rounded focus:outline-none focus:border-blue-500"
            value="8599" 
            readOnly
            placeholder=" "
          />
          <label className="absolute left-2 top-0 text-blue-500 text-sm transition-all peer-placeholder-shown:top-2 peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400 peer-focus:top-0 peer-focus:text-sm peer-focus:text-blue-500 bg-white px-1">
            Building No
          </label>
        </div>
        
        <div className="relative">
          <input 
            className="peer p-2 pt-6 w-full border border-gray-300 rounded focus:outline-none focus:border-blue-500"
            value="Prince Saud Ibn Abdullah Ibn Jalawi Branch, AL JUBAIL" 
            readOnly
            placeholder=" "
          />
          <label className="absolute left-2 top-0 text-blue-500 text-sm transition-all peer-placeholder-shown:top-2 peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400 peer-focus:top-0 peer-focus:text-sm peer-focus:text-blue-500 bg-white px-1">
            City
          </label>
        </div>
        
        <div className="relative">
          <input 
            className="peer p-2 pt-6 w-full border border-gray-300 rounded focus:outline-none focus:border-blue-500"
            value="Tebah Dist." 
            readOnly
            placeholder=" "
          />
          <label className="absolute left-2 top-0 text-blue-500 text-sm transition-all peer-placeholder-shown:top-2 peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400 peer-focus:top-0 peer-focus:text-sm peer-focus:text-blue-500 bg-white px-1">
            District
          </label>
        </div>
        
        <div className="relative">
          <input 
            className="peer p-2 pt-6 w-full border border-gray-300 rounded focus:outline-none focus:border-blue-500"
            value="Kingdom of Saudi Arabia" 
            readOnly
            placeholder=" "
          />
          <label className="absolute left-2 top-0 text-blue-500 text-sm transition-all peer-placeholder-shown:top-2 peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400 peer-focus:top-0 peer-focus:text-sm peer-focus:text-blue-500 bg-white px-1">
            Country
          </label>
        </div>
        
        <div className="relative">
          <input 
            className="peer p-2 pt-6 w-full border border-gray-300 rounded focus:outline-none focus:border-blue-500"
            value="35513" 
            readOnly
            placeholder=" "
          />
          <label className="absolute left-2 top-0 text-blue-500 text-sm transition-all peer-placeholder-shown:top-2 peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400 peer-focus:top-0 peer-focus:text-sm peer-focus:text-blue-500 bg-white px-1">
            Postal Code
          </label>
        </div>
        
        <div className="relative">
          <input 
            className="peer p-2 pt-6 w-full border border-gray-300 rounded focus:outline-none focus:border-blue-500"
            value="314702232800003" 
            readOnly
            placeholder=" "
          />
          <label className="absolute left-2 top-0 text-blue-500 text-sm transition-all peer-placeholder-shown:top-2 peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400 peer-focus:top-0 peer-focus:text-sm peer-focus:text-blue-500 bg-white px-1">
            VAT Number
          </label>
        </div>
        
        <div className="relative">
          <input 
            className="peer p-2 pt-6 w-full border border-gray-300 rounded focus:outline-none focus:border-blue-500"
            value=" 7053932963" 
            readOnly
            placeholder=" "
          />
          <label className="absolute left-2 top-0 text-blue-500 text-sm transition-all peer-placeholder-shown:top-2 peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400 peer-focus:top-0 peer-focus:text-sm peer-focus:text-blue-500 bg-white px-1">
            CRN
          </label>
        </div>
      </div>

      {/* Client Details Section */}
      <h2 className="text-xl font-bold text-center mb-5 text-blue-900">
        Client Details
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="relative">
          <input
            type="text"
            name="clientName"
            value={selectedInvoice.clientDetails.clientName}
            onChange={(e) => handleInputChange(e, 'client')}
            className="peer p-2 pt-6 w-full border border-gray-300 rounded focus:outline-none focus:border-blue-500"
            placeholder=" "
            required
          />
          <label className="absolute left-2 top-0 text-blue-500 text-sm transition-all peer-placeholder-shown:top-2 peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400 peer-focus:top-0 peer-focus:text-sm peer-focus:text-blue-500 bg-white px-1">
            Name
          </label>
        </div>

        <div className="relative">
          <input
            name="clientAddress"
            value={selectedInvoice.clientDetails.clientAddress}
            onChange={(e) => handleInputChange(e, 'client')}
            className="peer p-2 pt-6 w-full border border-gray-300 rounded focus:outline-none focus:border-blue-500"
            placeholder=" "
          />
          <label className="absolute left-2 top-0 text-blue-500 text-sm transition-all peer-placeholder-shown:top-2 peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400 peer-focus:top-0 peer-focus:text-sm peer-focus:text-blue-500 bg-white px-1">
            Address
          </label>
        </div>

        <div className="relative">
          <input
            name="clientDistrict"
            value={selectedInvoice.clientDetails.clientDistrict}
            onChange={(e) => handleInputChange(e, 'client')}
            className="peer p-2 pt-6 w-full border border-gray-300 rounded focus:outline-none focus:border-blue-500"
            placeholder=" "
          />
          <label className="absolute left-2 top-0 text-blue-500 text-sm transition-all peer-placeholder-shown:top-2 peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400 peer-focus:top-0 peer-focus:text-sm peer-focus:text-blue-500 bg-white px-1">
            District
          </label>
        </div>

        <div className="relative">
          <input
            name="clientPostalCode"
            value={selectedInvoice.clientDetails.clientPostalCode}
            onChange={(e) => handleInputChange(e, 'client')}
            className="peer p-2 pt-6 w-full border border-gray-300 rounded focus:outline-none focus:border-blue-500"
            placeholder=" "
          />
          <label className="absolute left-2 top-0 text-blue-500 text-sm transition-all peer-placeholder-shown:top-2 peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400 peer-focus:top-0 peer-focus:text-sm peer-focus:text-blue-500 bg-white px-1">
            Postal Code
          </label>
        </div>

        <div className="relative">
          <input
            name="clientCountry"
            value={selectedInvoice.clientDetails.clientCountry}
            onChange={(e) => handleInputChange(e, 'client')}
            className="peer p-2 pt-6 w-full border border-gray-300 rounded focus:outline-none focus:border-blue-500"
            placeholder=" "
          />
          <label className="absolute left-2 top-0 text-blue-500 text-sm transition-all peer-placeholder-shown:top-2 peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400 peer-focus:top-0 peer-focus:text-sm peer-focus:text-blue-500 bg-white px-1">
            Country
          </label>
        </div>

        <div className="relative">
          <input
            name="clientAddNo"
            value={selectedInvoice.clientDetails.clientAddNo}
            onChange={(e) => handleInputChange(e, 'client')}
            className="peer p-2 pt-6 w-full border border-gray-300 rounded focus:outline-none focus:border-blue-500"
            placeholder=" "
          />
          <label className="absolute left-2 top-0 text-blue-500 text-sm transition-all peer-placeholder-shown:top-2 peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400 peer-focus:top-0 peer-focus:text-sm peer-focus:text-blue-500 bg-white px-1">
            Addl. No
          </label>
        </div>

        <div className="relative">
          <input
            name="clientVatNumber"
            value={selectedInvoice.clientDetails.clientVatNumber}
            onChange={(e) => handleInputChange(e, 'client')}
            className="peer p-2 pt-6 w-full border border-gray-300 rounded focus:outline-none focus:border-blue-500"
            placeholder=" "
          />
          <label className="absolute left-2 top-0 text-blue-500 text-sm transition-all peer-placeholder-shown:top-2 peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400 peer-focus:top-0 peer-focus:text-sm peer-focus:text-blue-500 bg-white px-1">
            VAT Number
          </label>
        </div>

        <div className="relative">
          <input
            name="clientCRN"
            value={selectedInvoice.clientDetails.clientCRN}
            onChange={(e) => handleInputChange(e, 'client')}
            className="peer p-2 pt-6 w-full border border-gray-300 rounded focus:outline-none focus:border-blue-500"
            placeholder=" "
          />
          <label className="absolute left-2 top-0 text-blue-500 text-sm transition-all peer-placeholder-shown:top-2 peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400 peer-focus:top-0 peer-focus:text-sm peer-focus:text-blue-500 bg-white px-1">
            CRN
          </label>
        </div>
      </div>

      {/* Invoice Details Section */}
      <h2 className="text-xl font-bold text-center mb-5 text-blue-900">
        Invoice Details
      </h2>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="relative">
          <input
            value={selectedInvoice.invoiceNumber}
            readOnly
            className="peer p-2 pt-6 w-full border border-gray-300 rounded focus:outline-none focus:border-blue-500"
            placeholder=" "
          />
          <label className="absolute left-2 top-0 text-blue-500 text-sm transition-all peer-placeholder-shown:top-2 peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400 peer-focus:top-0 peer-focus:text-sm peer-focus:text-blue-500 bg-white px-1">
            Invoice Number
          </label>
        </div>

        <div className="relative">
          <input
            type="date"
            name="invoiceDate"
            value={selectedInvoice.invoiceDetails.invoiceDate}
            onChange={(e) => handleInputChange(e, 'invoice')}
            className="peer p-2 pt-6 w-full border border-gray-300 rounded focus:outline-none focus:border-blue-500"
            placeholder=" "
          />
          <label className="absolute left-2 top-0 text-blue-500 text-sm transition-all peer-placeholder-shown:top-2 peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400 peer-focus:top-0 peer-focus:text-sm peer-focus:text-blue-500 bg-white px-1">
            Invoice Date
          </label>
        </div>

        <div className="relative">
          <input
            type="date"
            name="supplyDate"
            value={selectedInvoice.invoiceDetails.supplyDate}
            onChange={(e) => handleInputChange(e, 'invoice')}
            className="peer p-2 pt-6 w-full border border-gray-300 rounded focus:outline-none focus:border-blue-500"
            placeholder=" "
          />
          <label className="absolute left-2 top-0 text-blue-500 text-sm transition-all peer-placeholder-shown:top-2 peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400 peer-focus:top-0 peer-focus:text-sm peer-focus:text-blue-500 bg-white px-1">
            Supply Date
          </label>
        </div>

        <div className="relative">
          <input
            name="contractNo"
            value={selectedInvoice.invoiceDetails.contractNo}
            onChange={(e) => handleInputChange(e, 'invoice')}
            className="peer p-2 pt-6 w-full border border-gray-300 rounded focus:outline-none focus:border-blue-500"
            placeholder=" "
          />
          <label className="absolute left-2 top-0 text-blue-500 text-sm transition-all peer-placeholder-shown:top-2 peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400 peer-focus:top-0 peer-focus:text-sm peer-focus:text-blue-500 bg-white px-1">
            Contract / PO No
          </label>
        </div>

        <div className="relative">
          <input
            name="invoicePeriod"
            value={selectedInvoice.invoiceDetails.invoicePeriod}
            onChange={(e) => handleInputChange(e, 'invoice')}
            className="peer p-2 pt-6 w-full border border-gray-300 rounded focus:outline-none focus:border-blue-500"
            placeholder=" "
          />
          <label className="absolute left-2 top-0 text-blue-500 text-sm transition-all peer-placeholder-shown:top-2 peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400 peer-focus:top-0 peer-focus:text-sm peer-focus:text-blue-500 bg-white px-1">
            Invoice Period
          </label>
        </div>

        <div className="relative">
          <input
            name="projectRefNo"
            value={selectedInvoice.invoiceDetails.projectRefNo}
            onChange={(e) => handleInputChange(e, 'invoice')}
            className="peer p-2 pt-6 w-full border border-gray-300 rounded focus:outline-none focus:border-blue-500"
            placeholder=" "
          />
          <label className="absolute left-2 top-0 text-blue-500 text-sm transition-all peer-placeholder-shown:top-2 peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400 peer-focus:top-0 peer-focus:text-sm peer-focus:text-blue-500 bg-white px-1">
            Project / Reference No
          </label>
        </div>
      </div>

      {/* Products Section */}
      <h2 className="text-xl font-bold text-center mb-5 text-blue-900">
        Products
      </h2>

      {selectedInvoice.products.map((product, index) => (
        <div key={index} className="mb-5 p-4 border rounded-lg">
          <h4 className="font-medium mb-3">Product {index + 1}</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="col-span-3">
              <div className="flex items-center gap-2">
                <div className="relative flex-grow">
                  <input
                    type="text"
                    name="natureOfGoods"
                    value={product.natureOfGoods}
                    onChange={(e) => handleProductChange(e, index)}
                    className="peer p-2 pt-6 w-full border border-gray-300 rounded focus:outline-none focus:border-blue-500"
                    placeholder=" "
                  />
                  <label className="absolute left-2 top-0 text-blue-500 text-sm transition-all peer-placeholder-shown:top-2 peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400 peer-focus:top-0 peer-focus:text-sm peer-focus:text-blue-500 bg-white px-1">
                    Nature of Goods
                  </label>
                </div>
              
              </div>
              {translatedFields[`natureOfGoods_${index}`] && (
                <div className="mt-2 p-2 bg-gray-100 rounded">
                  <p className="text-sm text-gray-600">Translated:</p>
                  <p>{translatedFields[`natureOfGoods_${index}`]}</p>
                </div>
              )}
            </div>
            
            <div className="relative">
              <input
                type="number"
                name="unitRate"
                value={product.unitRate}
                onChange={(e) => handleProductChange(e, index)}
                className="peer p-2 pt-6 w-full border border-gray-300 rounded focus:outline-none focus:border-blue-500"
                placeholder=" "
              />
              <label className="absolute left-2 top-0 text-blue-500 text-sm transition-all peer-placeholder-shown:top-2 peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400 peer-focus:top-0 peer-focus:text-sm peer-focus:text-blue-500 bg-white px-1">
                Unit Rate
              </label>
            </div>
            
            <div className="relative">
              <input
                type="number"
                name="quantity"
                value={product.quantity}
                onChange={(e) => handleProductChange(e, index)}
                className="peer p-2 pt-6 w-full border border-gray-300 rounded focus:outline-none focus:border-blue-500"
                placeholder=" "
              />
              <label className="absolute left-2 top-0 text-blue-500 text-sm transition-all peer-placeholder-shown:top-2 peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400 peer-focus:top-0 peer-focus:text-sm peer-focus:text-blue-500 bg-white px-1">
                Quantity
              </label>
            </div>
            
            <div className="relative">
              <select
                name="unit"
                value={product.unit || 'UNIT'}
                onChange={(e) => handleProductChange(e, index)}
                className="peer p-2 pt-6 w-full border border-gray-300 rounded focus:outline-none focus:border-blue-500"
              >
                <option value="UNIT">UNIT</option>
                <option value="HOURLY">HOURLY</option>
                 <option value="DAYS">DAYS</option>
                 <option value="METER">METER</option>
              </select>
              <label className="absolute left-2 top-0 text-blue-500 text-sm transition-all peer-placeholder-shown:top-2 peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400 peer-focus:top-0 peer-focus:text-sm peer-focus:text-blue-500 bg-white px-1">
                Unit
              </label>
            </div>
            
            <div className="relative">
              <input
                type="number"
                name="vatRate"
                value={product.vatRate}
                onChange={(e) => handleProductChange(e, index)}
                className="peer p-2 pt-6 w-full border border-gray-300 rounded focus:outline-none focus:border-blue-500"
                placeholder=" "
              />
              <label className="absolute left-2 top-0 text-blue-500 text-sm transition-all peer-placeholder-shown:top-2 peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400 peer-focus:top-0 peer-focus:text-sm peer-focus:text-blue-500 bg-white px-1">
                VAT Rate
              </label>
            </div>
            
            <div className="col-span-2">
              <div className="flex items-center gap-2">
                <div className="relative flex-grow">
                  <input
                    name="productDescription"
                    value={product.productDescription || ''}
                    onChange={(e) => handleProductChange(e, index)}
                    className="peer p-2 pt-6 w-full border border-gray-300 rounded focus:outline-none focus:border-blue-500"
                    placeholder=" "
                  />
                  <label className="absolute left-2 top-0 text-blue-500 text-sm transition-all peer-placeholder-shown:top-2 peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400 peer-focus:top-0 peer-focus:text-sm peer-focus:text-blue-500 bg-white px-1">
                    Final Amount In Words
                  </label>
                </div>
               
              </div>
              {translatedFields[`productDescription_${index}`] && (
                <div className="mt-2 p-2 bg-gray-100 rounded">
                  <p className="text-sm text-gray-600">Translated:</p>
                  <p>{translatedFields[`productDescription_${index}`]}</p>
                </div>
              )}
            </div>
          </div>
          
          <div className="mt-3">
            <p>Total Excl. VAT: {product.totalExclVAT} SAR</p>
            <p>VAT Amount: {product.vatAmount} SAR</p>
            <p>Total Incl. VAT: {product.totalInclVAT} SAR</p>
          </div>
          
          <div className="flex justify-end mt-3">
            <button
              className="bg-red-500 text-white p-2 rounded"
              onClick={() => removeProductRow(index)}
            >
              <DeleteIcon />
            </button>
          </div>
        </div>
      ))}

      {/* Add product row button */}
      <button
        className="bg-blue-600 text-white px-4 py-2 rounded mb-6"
        onClick={addProductRow}
      >
        Add Product
      </button>

      {/* Totals for all products */}
      <div className="mt-5 p-4 border rounded-lg">
        <h3 className="font-bold text-lg text-blue-900 mb-3">Totals</h3>
        <p>Total Excl. VAT: {selectedInvoice.totals.totalExclVAT} SAR</p>
        <p>Total VAT: {selectedInvoice.totals.totalVAT} SAR</p>
        <p>Total Incl. VAT: {selectedInvoice.totals.totalInclVAT} SAR</p>
        <p>Balance Due: {selectedInvoice.totals.balanceDue} SAR</p>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-3 pt-4 border-t">
        <button
          className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-100"
          onClick={handleDialogClose}
        >
          Cancel
        </button>
        <button
          className="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700"
          onClick={printInvoice}
        >
          Print Invoice
        </button>
        <button
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          onClick={handleUpdateInvoice}
        >
          Save Changes
        </button>
      </div>
    </div>
  </div>
)}
    </div>
    </div>
    </ThemeProvider>
  );
};

export default InvoiceList;